import { useState } from 'react';

import { Button, Dropdown, Tooltip, Whisper } from 'rsuite';

import classNames from 'classnames';
import { format } from 'date-fns';
import { MdDownload, MdGroupWork, MdInfo } from 'react-icons/md';
import { CustomHeaderCell, InfoTable, Skeleton } from '../../../../../../../components';
import { formatNumbersToPtBr, styleFormat } from '../../../../../../../utils/FormatNumbers';
import { useHistoryPrices } from '../../../../hooks/useHistoryPrices';
import { TableData } from '../../../../types/service';
import styles from './TableHistory.module.scss';

const lengthMenu = [
  {
    value: 5,
    label: 5,
  },
  {
    value: 10,
    label: 10,
  },
  {
    value: 20,
    label: 20,
  },
];

const STATUS = {
  acatado: 'acatado',
  nao_acatado: 'não acatado',
};

const optionsPercent = {
  style: 'percent' as styleFormat,
  options: {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
    showSuffix: true,
  },
};

const optionsDecimal = {
  style: 'decimal' as styleFormat,
  options: {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    showSuffix: false,
  },
};
const optionsCurrency = {
  style: 'currency' as styleFormat,
  options: {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    showSuffix: true,
  },
};

const priceDisplayOptions = {
  percent: optionsPercent,
  decimal: optionsDecimal,
  currency: optionsCurrency,
};

type StoreData = TableData;

function formatNumber(value: number, style: styleFormat) {
  return formatNumbersToPtBr(value, style, priceDisplayOptions[style as keyof typeof priceDisplayOptions].options);
}

interface Props {
  isLoading?: boolean;
}
export const TableHistory = ({ isLoading }: Props) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { states, handlers } = useHistoryPrices();
  const { handleChangePage, handleChangeSize, handleSetSortColumn, handleDownload, onChangeSort } = handlers;
  const { tablePagination, tableData, sortOptions, datakeys } = states;

  const paginationData = {
    total: tablePagination.totalElements,
    activePage: tablePagination.page + 1,
    displayLength: tablePagination.size,
    lengthMenu,
    onChangePage: handleChangePage,
    onChangeLength: handleChangeSize,
  };

  const { Column, HeaderCell, Cell } = InfoTable;
  const DATA_KEY = 'optimization-history-tab';

  if (isLoading) {
    return <Skeleton height={500} />;
  }

  const generateVariationSign = (variation: number) => {
    if (variation > 0) {
      return { signal: 'positive', label: '+' };
    }
    if (variation < 0) {
      return { signal: 'negative', label: '-' };
    }
    return { signal: 'neutral', label: 'sem var.' };
  };

  return (
    <div className={styles.container}>
      <div className={styles['header']}>
        <p>Histórico de preços</p>

        <div>
          <Dropdown
            placement="bottomEnd"
            className={styles['more-options']}
            renderTitle={() => (
              <Button appearance="ghost" onClick={() => setShowTooltip(!showTooltip)}>
                <MdDownload size={18} />
                Download
              </Button>
            )}
          >
            <Dropdown.Item onClick={() => handleDownload('csv')} id="btn-csv">
              Download em CSV
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleDownload('xlsx')} id="btn-xls">
              Download em XLSX
            </Dropdown.Item>
          </Dropdown>
        </div>
      </div>
      <InfoTable
        id={DATA_KEY}
        data={tableData}
        dataKey={DATA_KEY}
        className={styles.table}
        loading={isLoading}
        minHeight={200}
        headerHeight={34}
        rowHeight={62}
        sortColumn={tablePagination.sortColumn}
        sortType={tablePagination.orderBy || 'asc'}
        onSortColumn={handleSetSortColumn}
        pagination={paginationData}
      >
        {/* @ts-ignore */}
        <Column minWidth={200} flexGrow={2} sortable>
          <HeaderCell>
            <CustomHeaderCell dataKey={datakeys.product} data={sortOptions.product} onChangeSort={onChangeSort}>
              Produto
            </CustomHeaderCell>
          </HeaderCell>
          <Cell dataKey={datakeys.product}>
            {(rowData: StoreData) => (
              <Whisper trigger="hover" placement="right" speaker={<Tooltip>{rowData.product.productName}</Tooltip>}>
                <div className={classNames(styles.column, styles['cell-product'])}>
                  <div className={styles.store}>
                    <p>{rowData.product.productId}</p>
                    <span>
                      <MdGroupWork color="#378EF0" size={10} />
                      {rowData.familyId}
                    </span>
                  </div>
                  <p>{rowData.product.productName}</p>
                </div>
              </Whisper>
            )}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column minWidth={100} flexGrow={1} sortable>
          <HeaderCell>Lojas</HeaderCell>
          <Cell dataKey="storeName">
            {(rowData: StoreData) => (
              <Whisper trigger="hover" placement="right" speaker={<Tooltip>{rowData.store.storeName}</Tooltip>}>
                <div className={classNames(styles.column, styles['cell-store'])}>
                  <div>{rowData.store.storeId}</div>

                  <p>{rowData.store.storeName}</p>
                </div>
              </Whisper>
            )}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column width={100} flexGrow={1} sortable>
          <HeaderCell>Data</HeaderCell>
          <Cell dataKey="appliedAt">
            {(rowData: StoreData) => (
              <div className={styles.column}>
                <div className={styles['cell-value-date']}>{format(new Date(`${rowData.date}T00:00:00`), 'dd/MM/yyyy')}</div>
              </div>
            )}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column width={150} flexGrow={1} sortable>
          <HeaderCell>
            <div className={styles.header}>
              <span>Preço anterior</span>
              <Whisper
                trigger="hover"
                placement="bottom"
                speaker={<Tooltip>Valor do produto praticado antes da aplicação da otimização, registrado no período anterior</Tooltip>}
              >
                <MdInfo />
              </Whisper>
            </div>
          </HeaderCell>
          <Cell dataKey="retailPrice">
            {(rowData: StoreData) => (
              <div className={styles.column}>
                <div className={styles['cell-value-number']}>{formatNumber(rowData.previousPrice, 'currency')}</div>
              </div>
            )}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column width={150} flexGrow={1} sortable>
          <HeaderCell>
            <div className={styles.header}>
              <span>Preço sugerido</span>
              <Whisper
                trigger="hover"
                placement="bottom"
                speaker={<Tooltip>Valor recomendado pelo algoritmo de otimização, com base na estratégia selecionada</Tooltip>}
              >
                <MdInfo />
              </Whisper>
            </div>
          </HeaderCell>
          <Cell dataKey="optimizedPrice">
            {(rowData: StoreData) => (
              <div className={styles.column}>
                <div className={styles['cell-value-number']}>{formatNumber(rowData.suggestedPrice, 'currency')}</div>
              </div>
            )}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column width={150} flexGrow={1} sortable>
          <HeaderCell>Variação (R$)</HeaderCell>
          <Cell dataKey="variationPrice">
            {(rowData: StoreData) => {
              const variationSign = generateVariationSign(rowData.variationPercentage);
              return (
                <div className={styles.row}>
                  <div className={styles['cell-value-number']}>{formatNumber(rowData.variation, 'currency')}</div>
                  <div className={styles.badge} data-variation={variationSign.signal}>
                    <span className={styles['variation-sign']}>{variationSign.label}</span>

                    {variationSign.signal === 'neutral' ? null : <span>{formatNumber(Math.abs(rowData.variationPercentage), 'percent')}</span>}
                  </div>
                </div>
              );
            }}
          </Cell>
        </Column>
        {/* @ts-ignore */}
        <Column width={128} flexGrow={1} sortable>
          <HeaderCell>
            <div className={styles.header}>
              <span>Status otimização</span>
              <Whisper
                trigger="hover"
                placement="bottom"
                speaker={<Tooltip>Indica se o preço sugerido foi acatado (implementado na loja) ou não acatado (não aplicado)</Tooltip>}
              >
                <MdInfo />
              </Whisper>
            </div>
          </HeaderCell>
          <Cell dataKey="status">
            {(rowData: StoreData) => (
              <div className={classNames(styles.column, styles.status)} data-status={rowData.statusOptimization}>
                <div>{STATUS[rowData.statusOptimization as keyof typeof STATUS] || 'não acatado'}</div>
              </div>
            )}
          </Cell>
        </Column>
        {/* @ts-ignore */}
        <Column minWidth={200} flexGrow={1} sortable>
          <HeaderCell>Responsável</HeaderCell>
          <Cell dataKey="appliedBy">
            {(rowData: StoreData) => (
              <div className={classNames(styles.column, styles.responsible)}>
                <div>{rowData.responsible}</div>
              </div>
            )}
          </Cell>
        </Column>
      </InfoTable>
    </div>
  );
};
